'use client';

import Link from 'next/link';
import { Card, CardContent, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { treatments } from '@/lib/treatments-data';
import Breadcrumbs from '@/components/Breadcrumbs';
import OptimizedImage from '@/components/optimized-media/OptimizedImage';
import { useTranslation } from '@/hooks/useTranslation';

export default function TreatmentsPageContent() {
    const { t } = useTranslation('treatments');
    
    return (
        <div>
            <div className="relative h-[50vh] bg-gradient-to-r from-blue-900 to-blue-700 flex items-center justify-center">
                <div className="absolute inset-0 overflow-hidden">
                    <img
                        src="/images/env/treatments.webp"
                        alt="DentaWista Treatments"
                        className="w-full h-full object-cover opacity-20"
                    />
                </div>
                <div className="container relative z-10 text-center">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-display">
                        {t('title')}
                    </h1>
                    <p className="text-xl text-white/90 max-w-2xl mx-auto">
                        {t('subtitle')}
                    </p>
                </div>
            </div>

            <div className="container py-16">
                <Breadcrumbs />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {treatments.map((treatment) => (
                        <Card key={treatment.id} className="overflow-hidden transition-transform hover:-translate-y-1 flex flex-col h-full">
                            <div className="relative h-48 shrink-0">
                                {treatment.width && treatment.height ? (
                                    <OptimizedImage
                                        src={treatment.heroImage}
                                        alt={treatment.title}
                                        fill={true}
                                        style={{ objectFit: "cover" }}
                                    />
                                ) : (
                                    <img
                                        src={treatment.heroImage}
                                        alt={treatment.title}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                )}
                            </div>
                            <CardContent className="p-6 flex flex-col flex-1">
                                <CardTitle className="text-2xl font-bold mb-2 text-navy dark:text-white">{t(`cards.${treatment.id}.title`)}</CardTitle>
                                <CardDescription className="text-slate-600 dark:text-slate-300 mb-4 line-clamp-2">{t(`cards.${treatment.id}.description`)}</CardDescription>
                                <div className="space-y-2 flex-1">
                                    <div className="flex items-center text-sm text-slate-500 dark:text-slate-400">
                                        <span className="font-medium">{t('treatmentTime')}:</span>
                                        <span className="ml-2">{t(`cards.${treatment.id}.treatmentTime`)}</span>
                                    </div>
                                    <div className="flex items-center text-sm text-slate-500 dark:text-slate-400">
                                        <span className="font-medium">{t('successRate')}:</span>
                                        <span className="ml-2">{treatment.successRate}</span>
                                    </div>
                                </div>
                                <CardFooter className="p-0 pt-4 mt-auto">
                                    <Button asChild className="w-full bg-navy hover:bg-navy-800">
                                        <Link href={`/treatments/${treatment.slug}`}>
                                            {t('learnMore')}
                                        </Link>
                                    </Button>
                                </CardFooter>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    );
} 
